import { useContext } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type IBaseEntity } from "types/apiResponse/apiResponse";
import { type DiscussionsLinkedTypes } from "types/enum";

import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import {
    getLinkedEntityDetailPageLink,
    type IDiscussionLinkDetails,
} from "commonComponents/entity/comment/CommentComponent.utils";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

export interface IDiscussionLinkResponse extends IBaseEntity {
    entityType: DiscussionsLinkedTypes;
    commentCount: number;
    unreadCommentCount: number;
    onClick?: () => void;
    readOnly?: boolean;
    doNotNavigate?: boolean;
}

export class DiscussionLinkResponse implements IDiscussionLinkResponse {
    constructor(data: any) {
        this.id = data.id;
        this.entityType = data.entityType;
        this.commentCount = data.commentCount;
        this.unreadCommentCount = data.unreadCommentCount;
        this.jobId = data.jobId;
        this.builderId = data.builderId;
    }

    entityType: DiscussionsLinkedTypes;
    commentCount: number;
    unreadCommentCount: number;
    id: number;
    jobId: number | undefined;
    builderId: number;
}

function DiscussionLinkCompactInternal({
    id,
    entityType,
    builderId,
    jobId,
    commentCount,
    unreadCommentCount,
    readOnly,
    onClick,
    doNotNavigate,
}: IDiscussionLinkResponse) {
    const builderInfo = useContext(BuilderInfoContext);
    const linkContent = (
        <>
            {commentCount}
            {unreadCommentCount > 0 ? " (" + unreadCommentCount + " new)" : ""}
        </>
    );

    if (readOnly) {
        return linkContent;
    }

    const discussionLinkDetails: IDiscussionLinkDetails = {
        linkedType: entityType,
        linkedId: id,
        jobId: jobId ?? 0,
        builderId,
        builderInfo,
    };
    const to = getLinkedEntityDetailPageLink(discussionLinkDetails);

    if (!to.pathname && !doNotNavigate) {
        return linkContent;
    }

    if (doNotNavigate) {
        to.pathname = "";
        to.state = undefined;
    }

    if (!to.isRelativeLink) {
        return (
            <BTLink
                to={to.pathname}
                onClick={onClick}
                data-testid={`discussionLink-${commentCount}`}
            >
                {linkContent}
            </BTLink>
        );
    }

    return (
        <BTLinkRelative
            to={to.pathname}
            onClick={onClick}
            data-testid={`discussionLink-${commentCount}`}
        >
            {linkContent}
        </BTLinkRelative>
    );
}

export const DiscussionLinkCompact = withErrorBoundary(DiscussionLinkCompactInternal)(
    "Could not load Discussion",
);
